import { lazy, Suspense, useEffect } from 'react';
import Layout from '../layout-components/Layout';

/**
 * One shared import for prefetch and React.lazy.
 * A resolved module fulfills synchronously so the root loader stays off the skeleton.
 * @param {() => Promise<{ default: import('react').ComponentType }>} importer page import
 * @returns {{
 *   Page: import('react').LazyExoticComponent<import('react').ComponentType>,
 *   load: () => Promise<{ default: import('react').ComponentType }>,
 * }}
 */
export function lazyPage(importer) {
  /** @type {Promise<{ default: import('react').ComponentType }> | null} */
  let pending = null;
  /** @type {{ default: import('react').ComponentType } | null} */
  let resolved = null;

  const load = () => {
    if (!pending) {
      pending = importer().then((moduleObject) => {
        resolved = moduleObject;
        return moduleObject;
      });
    }
    return pending;
  };

  const Page = lazy(() => {
    if (resolved) {
      const moduleObject = resolved;
      return {
        then: (onFulfilled) => {
          onFulfilled(moduleObject);
        },
      };
    }
    return load();
  });

  return { Page, load };
}

/**
 * Mounts Pro, Store, and Maps immediately. Data hooks wait for a credential
 * and skeleton only their own lists.
 * @param {{
 *   component: import('react').ComponentType,
 *   load: () => Promise<unknown>,
 * }} props route component and its prefetch
 * @returns {import('react').ReactElement}
 */
const PublicCredentialRoute = ({ component: Page, load }) => {
  useEffect(() => {
    const pending = load();
    if (pending && typeof pending.catch === 'function') {
      pending.catch(() => null);
    }
  }, [load]);

  return (
    <Layout>
      <Suspense fallback={null}>
        <Page />
      </Suspense>
    </Layout>
  );
};

export default PublicCredentialRoute;
